// "Already in ConsLog?" (Kloepfer K8-6, Phelipe 2026-10-27): asked before a
// company is registered anywhere a person types one in — the Register New
// Company pop-up, Vendor Management → Add vendor, Owner's Reps. The server
// does the matching (name rules, then AI) and sends the pop-up's words in the
// user's language; this file only draws the pop-up and says what was picked.
import { track } from "./mixpanel"

const CHECK_URL = "/company/check_duplicate"

// → { duplicate: { id, name, details, source }, text: {…} } or null.
export async function checkCompanyDuplicate(name, win = window) {
  try {
    const resp = await win.fetch(CHECK_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": win.document.querySelector("meta[name='csrf-token']")?.content
      },
      body: JSON.stringify({ name })
    })
    if (!resp.ok) return null
    const data = await resp.json()
    return data?.duplicate ? data : null
  } catch {
    return null // the check never blocks a registration
  }
}

// Draws the pop-up over whatever is open → "existing" | "new" | "cancel".
// The backdrop and Escape cancel, like every other pop-up.
export function askAboutDuplicate(name, data, { screen = "", win = window } = {}) {
  const doc = win.document
  const text = data.text || {}
  const dup = data.duplicate
  return new Promise((resolve) => {
    const root = doc.createElement("div")
    root.className = "fixed inset-0 z-[70]"
    root.dataset.companyDuplicate = ""
    root.innerHTML = `
      <div class="fixed inset-0 bg-black/40"></div>
      <div class="fixed inset-0 flex items-center justify-center p-4">
        <div class="bg-white rounded-xl shadow-xl w-full max-w-md p-5" role="dialog" aria-modal="true" data-panel>
          <h3 class="text-base font-semibold mb-1" data-title></h3>
          <p class="text-[13px] text-gray-600 mb-3" data-body></p>
          <div class="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 mb-4">
            <p class="text-[13px] font-medium text-gray-800" data-existing-name></p>
            <p class="text-[13px] text-gray-500 mt-0.5" data-existing-details></p>
          </div>
          <div class="flex flex-col gap-2">
            <button type="button" data-choice="existing" class="w-full px-3 py-2 text-[13px] font-medium text-white bg-brand hover:bg-brand-dark rounded-lg cursor-pointer"></button>
            <button type="button" data-choice="new" class="w-full px-3 py-2 text-[13px] font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer"></button>
            <button type="button" data-choice="cancel" class="w-full px-3 py-1.5 text-[13px] text-gray-500 hover:bg-gray-100 rounded-lg cursor-pointer"></button>
          </div>
        </div>
      </div>`
    const find = (selector) => root.querySelector(selector)
    find("[data-title]").textContent = text.title || "Already in ConsLog?"
    const [before, after = ""] = (text.body || "%{name} looks a lot like a company that's already in ConsLog:").split("%{name}")
    const typed = doc.createElement("span")
    typed.className = "font-medium"
    typed.textContent = name
    find("[data-body]").append(before, typed, after)
    find("[data-existing-name]").textContent = dup.name
    find("[data-existing-details]").textContent = dup.details || ""
    find('[data-choice="existing"]').textContent = text.use_existing || "Use the existing company (recommended)"
    find('[data-choice="new"]').textContent = text.create_new || "No, this is a different company — create new"
    find('[data-choice="cancel"]').textContent = text.cancel || "Cancel"

    const onKey = (event) => {
      if (event.key !== "Escape") return
      event.stopPropagation()
      done("cancel")
    }
    const done = (choice) => {
      doc.removeEventListener("keydown", onKey, true)
      root.remove()
      track("Company Duplicate Answered", { screen, source: dup.source, choice })
      resolve(choice)
    }
    root.addEventListener("click", (event) => {
      const button = event.target.closest("[data-choice]")
      if (button) return done(button.dataset.choice)
      if (!event.target.closest("[data-panel]")) done("cancel")
    })
    doc.addEventListener("keydown", onKey, true)
    doc.body.appendChild(root)
    find('[data-choice="existing"]').focus()
  })
}

// Both steps → { choice: "existing" | "new" | "cancel", company }.
// Nothing found means "new" without a pop-up.
export async function confirmNewCompany(name, { screen = "", win = window } = {}) {
  const data = await checkCompanyDuplicate(name, win)
  if (!data) return { choice: "new" }
  const choice = await askAboutDuplicate(name, data, { screen, win })
  return { choice, company: data.duplicate }
}
